import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import withWidth from '@material-ui/core/withWidth';
import { HubConnectionBuilder, HubConnectionState, LogLevel } from "@microsoft/signalr";
import IdleTimer from 'react-idle-timer'
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
// import { useBeforeunload } from 'react-beforeunload';
import Typography from '@material-ui/core/Typography';

import initiateConnection from './actions/connection_actions';
import getProfileFetch from './actions/profile_actions';
import userActions from './actions/user_actions';
import memberActions from './actions/member_actions';
import unreadActions from './actions/unread_message_actions';
import windowActions from './actions/window_actions';
import preferenceActions from './actions/preference_actions';
import LoginPage from './components/LoginPage';
import MainSideBar from './components/MainSideBar';
import { logoutUser } from './actions/logout_actions';
import { closeNamedWindow } from './utils/popupWindows';
import { FIXED_CHILD_WINDOWS } from './utils/windowNames';
import { canNotify, noWindowFocused, showMessageNotification, showAnnouncementNotification } from './utils/desktopNotifications';

import './index.css';
import { flashApp } from './utils/flashNotification';
import { isOn } from './utils/notificationPreferences';

// How stale LastSeen is allowed to get before the server's presence sweeps
// call someone Disconnected is Presence.StaleAfterMs (5 minutes) - keep
// PING_INTERVAL_MS well inside it.
//
// The interval is checked against the wall clock rather than trusted to fire
// on time. setInterval is a main-thread timer: at 100% CPU, or in a tab Chrome
// has throttled for being in the background, ticks arrive late and coalesce
// instead of queueing, so a 30s timer was silently going minutes between
// pings. Polling more often and comparing Date.now() means the first tick
// after the machine frees up pings immediately, rather than starting another
// full interval from wherever the timer happened to land.
const PING_INTERVAL_MS = 30000;
const PING_CHECK_INTERVAL_MS = 5000;

// The first hub start, and every restart after an outright close, retries on
// a doubling delay. A server that is down for a deploy is better asked every
// half minute than hammered, and a blip is retried within a couple of seconds.
const RESTART_BASE_DELAY_MS = 2000;
const RESTART_MAX_DELAY_MS = 30000;

export const restartDelay = (attempt) =>
    Math.min(RESTART_MAX_DELAY_MS, RESTART_BASE_DELAY_MS * Math.pow(2, Math.max(0, attempt)));

// How long the "page will refresh" dialog counts down, and how often the
// reachability probe asks whether the server answers again.
const NETWORK_COUNTDOWN_SECONDS = 10;
const REACHABILITY_PROBE_MS = 2000;

// Asks the TeamX server itself whether it can be reached. This used to fetch
// //google.com, which on a network that blocks Google never answered, so the
// "page will refresh" dialog stayed up for good. "/" is index.html in both
// modes - the CRA dev server in Development, the SPA default page otherwise -
// and Startup.SetSpaCacheHeaders serves it no-cache, so HEAD with no-store is
// a bodiless revalidation that cannot be answered from a cache. Any HTTP
// answer at all means the network is back; only a network error means it is
// not.
export const probeServer = (fetchImpl) =>
    (fetchImpl || fetch)('/', { method: 'HEAD', cache: 'no-store', credentials: 'same-origin' })
        .then(() => true, () => false);

// unload logs the user out, because closing the page is how people leave.
// A reload the app starts itself after a network drop is not someone
// leaving, so it sets this first and unload lets the session be - the same
// way justLoggedIn spares the reload that follows a sign-in. Without it a
// ten-second outage signed everyone out.
export const RELOAD_KEEPS_SESSION_KEY = 'reloadKeepsSession';

// The ids SaveUnreadMessages should hold, from the unreadMessages map. Keys
// containing "Object" are skipped exactly as the inline loop this came out
// of always skipped them.
export const unreadIdsFrom = (unreadMessages) => {
    var l = [];
    for (var property in unreadMessages) {
        if (property.includes("Object")) {
            continue;
        }
        if (unreadMessages[property]) {
            l.push(property);
        }
    }
    return l;
};

export class App extends Component {
    constructor(props) {
        super(props);
        this.idleTimer1 = null;
        this.timeLeftInterval = null;
        this.networkTimeLeftInterval = null;
        this.pingTimer = null;
        this.lastPingAt = 0;
        // Hub lifecycle. hubBound says the handlers, the ping timer and
        // state.hub have been set up - once, on whichever start succeeds
        // first. restartTimer is the one pending retry, so a close and a
        // failed start racing each other cannot queue two.
        this.hubBound = false;
        this.restartTimer = null;
        this.restartAttempt = 0;
        this.unmounted = false;
        // The reachability probe's interval, and whether a probe is out.
        this.webPingInterval = null;
        this.probeInFlight = false;
        this.state = {
            openIsActiveDialog: false,
            openUpdateStatusDialog: true,
            isDisconnected: false,
            networkSecondsLeft: null,
        };
        this.connection = new HubConnectionBuilder()
            .withUrl("/notifications", { accessTokenFactory: () => localStorage.token })
            // Default is Information, which logs every negotiation - including
            // the socket URL with the JWT in its query string - to the console.
            .configureLogging(LogLevel.Warning)
            .withAutomaticReconnect({
                nextRetryDelayInMilliseconds: retryContext => {
                    return Math.random() * 10000;
                }
            })
            .build();
    }

    
    // Was three copies of an if that tested all six windows for null and then
    // re-opened each one to close it - twelve window.open calls, and all-or-
    // nothing, so one blocked window left the other five open. Note window.open
    // with an empty URL CREATES a window that does not exist, so this has always
    // opened blank windows just to close them again; that is preserved here
    // rather than quietly changed on a release night.
    //
    // The names come from utils/windowNames. App used to keep its own list,
    // and four of its six were misspelt ('ChatBoxWindow' for 'chatBoxWindow',
    // 'UserManager' for 'User Manager', ...) with the Quote Manager missing,
    // so logout opened and closed a blank pop-up for each and left the real
    // window open. Announcement windows are named per message and are closed
    // by whoever holds the announcements.
    closeChildWindows = () => {
        FIXED_CHILD_WINDOWS.forEach(name => closeNamedWindow(name));
    };

    // Raises a desktop notification for an incoming message, and reports
    // whether it did - the caller uses that to decide whether to chime.
    //
    // Quiet whenever the user can already see the message land: either TeamX
    // window having focus is enough. Also quiet when the sender is not someone
    // we can name, since "New message from undefined" is worse than nothing.
    notifyForMessage = (senderId, text) => {
        // Off means off. Returning false lets the caller fall back to the
        // chime, which is gated on its own preference.
        if (!isOn(this.props.preferences, 'notifications', 'desktop')) {
            return false;
        }

        if (!canNotify() || !noWindowFocused(this.props.chatWindow)) {
            return false;
        }

        const sender = this.props.memberIdMap ? this.props.memberIdMap[senderId] : null;
        const senderName = sender ? (sender.firstName || sender.username) : null;
        if (!senderName) {
            return false;
        }

        // Nothing is passed for a click: clicking a toast must never bring the
        // conversation forward. The body is passed only when the user has
        // asked for previews - off by default, because a toast fires exactly
        // when someone other than the reader may be looking at the screen.
        const note = showMessageNotification({
            senderName: senderName,
            senderId: senderId,
            body: isOn(this.props.preferences, 'notifications', 'preview') ? text : undefined,
        });

        return note !== null;
    };

    // Returns whether a toast fired, so the caller can chime instead - the
    // two stay mutually exclusive, exactly as they are for a message.
    //
    // Takes no title: the toast says "New announcement" and nothing more,
    // for the same reason a message toast names only the sender. The body
    // follows the same preview preference a message does - one switch, so a
    // user who has decided toasts may carry content has decided it once.
    notifyForAnnouncement = (text) => {
        if (!isOn(this.props.preferences, 'notifications', 'announcements')) {
            return false;
        }
        if (!isOn(this.props.preferences, 'notifications', 'desktop')) {
            return false;
        }
        if (!noWindowFocused(this.props.chatWindow)) {
            return false;
        }
        return showAnnouncementNotification({
            body: isOn(this.props.preferences, 'notifications', 'preview') ? text : undefined,
        }) !== null;
    };

    // Stamps LastSeen on the server. Guarded on the connection actually being
    // up: invoke() rejects while the hub is reconnecting, and a rejected ping
    // is a lost ping, which is what the sweeps then read as "gone".
    sendActivePing = () => {
        const userId = this.props.profile && this.props.profile.id;
        if (!userId || !this.connection || this.connection.state !== HubConnectionState.Connected) {
            return Promise.resolve();
        }
        this.lastPingAt = Date.now();
        return this.connection.invoke('SendUserActivePing', userId).catch(err => {
            // The server never saw it, so do not let it count. Zeroing means
            // the next check retries, at most one attempt per check interval.
            this.lastPingAt = 0;
            return console.error("Pinging Error " + err.toString());
        });
    };

    // Starts the hub, and on failure hands over to restartConnection. The
    // first start used to be a one-shot: a failure only logged, and since
    // onclose, the handlers and the ping timer were all set up in its success
    // branch, a server that was briefly down at page load meant no live
    // messages and no pings until the user reloaded.
    startConnection = () => {
        return this.connection.start()
            .then(() => this.handleConnected())
            .catch(err => {
                console.error("Hub start failed: " + (err && err.toString()));
                this.restartConnection();
            });
    };

    // Keeps retrying rather than giving up after one attempt. A client that
    // stops pinging looks Disconnected to everyone else no matter how much the
    // person is actually using it. Backs off from 2s to 30s, and there is only
    // ever one retry pending.
    restartConnection = () => {
        if (this.restartTimer || this.unmounted) {
            return;
        }
        const delay = restartDelay(this.restartAttempt);
        this.restartAttempt += 1;
        this.restartTimer = setTimeout(() => {
            this.restartTimer = null;
            // Signed out in the meantime: the hub is [Authorize], so trying
            // again would only 401 forever. The next sign-in reloads the page.
            if (this.unmounted || !localStorage.token) {
                return;
            }
            // start() rejects unless the connection is Disconnected. If it
            // came up some other way, adopt it; if it is mid-reconnect, look
            // again later rather than fail against it.
            if (this.connection.state === HubConnectionState.Connected) {
                this.handleConnected();
                return;
            }
            if (this.connection.state !== HubConnectionState.Disconnected) {
                this.restartConnection();
                return;
            }
            this.startConnection();
        }, delay);
    };

    // Runs on every successful start. Everything that must happen once per
    // page - the hub handlers, onclose/onreconnected, the ping timer and
    // handing the connection to Redux - is behind hubBound. SignalR keeps
    // handlers registered with .on() across a stop and start of the same
    // HubConnection, so a second registration would deliver every
    // NewMessage twice.
    handleConnected = () => {
        this.restartAttempt = 0;
        if (!this.hubBound) {
            this.hubBound = true;
            this.bindHub();
        }
        this.sendActivePing();
    };

    // A swept user is announced to every client as "Active disconnected" on
    // SetUserStatus, with no id in it.
    handleSetUserStatus = (message) => {
        if (typeof message === 'string' && message.indexOf('disconnected') >= 0) {
            this.props.fetchMember();
        }
    };

    // An incoming message: chime or notify, mark the sender unread, and
    // persist the set.
    handleNewMessage = (message) => {
        // A desktop notification and the in-app chime would
        // otherwise both fire and you would hear two sounds, so
        // whichever one happens, happens alone. The notification
        // only goes out when the user is looking at neither
        // TeamX window - if they can already see the message
        // arrive, the chime is the right cue.
        if (message.payload.userId !== this.props.profile.id) {
            if (!this.notifyForMessage(message.payload.userId, message.payload.message)) {
                if (isOn(this.props.preferences, 'notifications', 'sound')) {
                    this.audio = new Audio('./light.mp3');
                    this.audio.play();
                }
            }
        }

        if (message.payload.userId !== this.props.profile.id) {
            // Reading this.props straight after the dispatch is correct here
            // and only here: a SignalR callback is not batched, so connect
            // has already re-rendered and props carry the new marker. (The
            // same read inside a click handler sees the old props - see
            // disableUnread in ChatBoxTabsWindow.)
            this.props.updateUnread(message);
            // SaveUnreadMessages replaces the caller's whole set. Until the
            // initial fetch has answered, props hold only what arrived since
            // the page loaded, so saving them wiped every other sender the
            // server had on record. Nothing is lost by skipping: the server
            // already wrote this sender's row when the message was sent
            // (PostController.SendMessage), and the initial fetch merges it
            // with the in-memory marker when it lands.
            if (this.props.profile.hasOwnProperty('id') && this.props.unreadLoaded) { // current signed in user
                this.props.setUnreadIds(unreadIdsFrom(this.props.unreadMessages));
            }
        }
    };

    bindHub = () => {
        this.props.initiateConnection(this.connection);

        // withAutomaticReconnect (see the constructor) handles the
        // ordinary drop and retries forever, so onclose only fires
        // if the connection is stopped outright. Retry with a catch
        // that re-arms: the old version was a single uncaught
        // start(), and because onclose does not fire again on an
        // already-closed connection, one failed attempt stranded
        // the client with no pings until a page reload.
        this.connection.onclose(error => {
            this.restartConnection();
        });

        // A reconnect leaves LastSeen unstamped for the whole
        // outage. Close that gap now instead of waiting out another
        // interval on top of it.
        this.connection.onreconnected(connectionId => {
            this.sendActivePing();
        });

        if (!this.pingTimer) {
            this.pingTimer = setInterval(this.maybeSendActivePing, PING_CHECK_INTERVAL_MS);
        }

        this.connection.on('UpdateUser', (user => {
            this.props.fetchMember();
            this.props.getProfileFetch();
        }));
        // This used to post the caller's whole profile back to
        // api/user/update on every sweep. The message goes to every client
        // and names nobody, so each of N clients posted, each post pushed
        // UpdateUser to all N, and each of those refetched the member list
        // and the profile - about 2N² requests per sweep. It was a
        // self-heal for the swept user, and it cannot work: whoever was
        // swept is by definition not the one still talking to the hub, and
        // SendUserActivePing now restores a returning user's status and
        // InOffice on the server and announces it with UpdateUser. What the
        // broadcast is still good for is telling everyone else that
        // someone's status changed, which only needs the member list.
        this.connection.on('SetUserStatus', this.handleSetUserStatus);
        // UserHub already subscribes to NewAnnouncement to render the list.
        // This second subscription owns the notification side, which needs the
        // chatWindow handle App holds; SignalR fans one event out to both.
        // The payload is the announcement Post, so its text is
        // here to be previewed. This handler used to take no
        // argument at all and throw it away.
        this.connection.on('NewAnnouncement', ((notification) => {
            if (!isOn(this.props.preferences, 'notifications', 'announcements')) {
                return;
            }
            if (flashApp(this.props.preferences, 'flashstart')) {
                this.props.setIconFlash(true);
            }
            var announced = notification && notification.payload
                ? notification.payload.message
                : undefined;
            if (!this.notifyForAnnouncement(announced)) {
                if (isOn(this.props.preferences, 'notifications', 'sound')) {
                    this.audio = new Audio('./light.mp3');
                    this.audio.play();
                }
            }
        }));

        this.connection.on('NewMessage', this.handleNewMessage);
    };

    maybeSendActivePing = () => {
        if (Date.now() - this.lastPingAt < PING_INTERVAL_MS) {
            return;
        }
        this.sendActivePing();
    };

    // Coming back to the tab is the moment throttling ends and the moment the
    // timestamp is most likely to be stale, so ping rather than wait for the
    // next check.
    handleVisibilityChange = () => {
        if (!document.hidden) {
            this.sendActivePing();
        }
    };

    componentDidMount = () => {
        // The taskbar flash lives in the native handler and survives a
        // reload; state.shouldTurnFlashOn does not. A page that loaded while
        // the handler was flashing believed nothing was, and reading the
        // message could not stop it. The page is on screen when it loads,
        // so stop here. Preferences are not fetched yet, and flashstop never
        // consults them anyway.
        flashApp(this.props.preferences, 'flashstop');
        this.handleConnectionChange();
        this.props.getProfileFetch();
        this.props.fetchPreferences();
        this.props.getUnreadMessagesFromDb();
        // The hub is [Authorize], so negotiating without a token 401s.
        // LoginPage reloads the page after a successful login, at which
        // point componentDidMount runs again and this connects for real.
        // A failed first start retries through restartConnection, and the
        // handlers, onclose and the ping timer are bound by handleConnected
        // on whichever start succeeds first.
        if (localStorage.token) {
            this.startConnection();
        }
        
        window.addEventListener("beforeunload", this.unload);
        window.addEventListener('online', this.handleConnectionChange);
        window.addEventListener('offline', this.handleConnectionChange);
        document.addEventListener('visibilitychange', this.handleVisibilityChange);

        var sheet = document.createElement('style');
        document.body.appendChild(sheet);
        sheet.innerHTML = '@media (max-width:767px){body{padding-right:0!important}.MuiOutlinedInput-multiline-327{padding:5px!important}.MuiInputBase-root-269{line-height:1.4em!important;padding:5px!important}.MuiDialog-paper-5{margin:5px!important}.MuiTypography-title-227{font-weight:600;font-size:20px}.emoji-mart-scroll{height:150px}.emoji-mart-preview{position:relative;height:60px}a#flashNotification{display:block;position:fixed;bottom:-1px;right:-1px;z-index:-1;opacity:0;width:1px;height:1px}}';
    }
    
    componentWillUnmount() {
        window.removeEventListener('beforeunload', this.unload);
        window.removeEventListener('online', this.handleConnectionChange);
        window.removeEventListener('offline', this.handleConnectionChange);
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
        clearInterval(this.pingTimer);
        this.pingTimer = null;
        this.unmounted = true;
        clearTimeout(this.restartTimer);
        this.restartTimer = null;
        this.stopReachabilityProbe();
        this.stopNetworkTimer();
    }

    // Starts probing the server every couple of seconds until it answers.
    // The interval used to be a local that was cleared only when a probe
    // succeeded, so every 'online' event stacked another one; it now lives
    // on this, and a probe still out is not overtaken by the next tick.
    startReachabilityProbe = () => {
        if (this.webPingInterval) {
            return;
        }
        this.webPingInterval = setInterval(() => {
            if (this.probeInFlight) {
                return;
            }
            this.probeInFlight = true;
            probeServer().then(reachable => {
                this.probeInFlight = false;
                if (this.unmounted) {
                    return;
                }
                if (reachable) {
                    this.stopReachabilityProbe();
                    this.stopNetworkTimer();
                    this.setState({ isDisconnected: false }, () => {
                        // Network is back, so LastSeen is as stale as
                        // the outage was long.
                        this.sendActivePing();
                    });
                    return;
                }
                // The dialog this shows promises a refresh, so the
                // countdown starts with it. It never did here, and the
                // dialog sat with an empty number for good.
                this.setState({ isDisconnected: true }, () => { this.networkStartTimer(); });
            });
        }, REACHABILITY_PROBE_MS);
    };

    stopReachabilityProbe = () => {
        clearInterval(this.webPingInterval);
        this.webPingInterval = null;
    };

    handleConnectionChange = () => {
        const condition = navigator.onLine ? 'online' : 'offline';
        if (condition === 'online') {
            this.startReachabilityProbe();
            return;
        }
        // Nothing can answer while the browser says it is offline; the
        // 'online' event starts probing again.
        this.stopReachabilityProbe();
        return this.setState({ isDisconnected: true }, () => { this.networkStartTimer(); });
    };

    // Every reload the app starts after a network drop goes through here,
    // so that unload does not treat it as the user leaving.
    reloadKeepingSession = (delayMs) => {
        try {
            localStorage.setItem(RELOAD_KEEPS_SESSION_KEY, 'true');
        } catch (e) {
            // Storage unavailable: the reload still happens, and logs out
            // as it always did.
        }
        setTimeout(() => window.location.reload(), delayMs);
    };

    unload = (ev) => {
        if (localStorage.getItem('justLoggedIn')) {
            localStorage.removeItem("justLoggedIn");
        } else if (localStorage.getItem(RELOAD_KEEPS_SESSION_KEY)) {
            // A network-driven reload, not someone closing the page: keep
            // the token and the status. The new page sends its own
            // flashstop on mount.
            localStorage.removeItem(RELOAD_KEEPS_SESSION_KEY);
        } else {
            // Leaving must not leave the taskbar flashing with nothing left
            // on this side that knows to stop it.
            flashApp(this.props.preferences, 'flashstop');
            if (this.props.profile.hasOwnProperty('id')) {
                var updatedUser = { ...this.props.profile };
                updatedUser["status"] = "Done";
                updatedUser["inOffice"] = false;
                updatedUser["stamp"] = "validLogout";
                // keepalive: a plain fetch started from beforeunload is
                // cancelled with the page, so the "Done" status often never
                // reached the server and the user stayed Available after
                // closing TeamX.
                this.props.updateProfile(updatedUser, { keepalive: true });
                this.props.updateLastSeen(true, { keepalive: true });
            } else {
                return ev;
            }
            localStorage.removeItem("token");
            this.props.setChatDisplay(false);
            //
            this.closeChildWindows();
            //
        }
    }
    
    /*
    
    // Chat Window Styling
    jss675 {padding 0px}
    jss673 {padding 18.5px 14px}
    jss281 {resize none; padding 0px}
    jss279 {font inherit; color currentcolor...}
    
    MuiOutlinedInput-inputMultiline-331 {padding 0px}
    MuiOutlinedInput-input-329 {padding 18.5px 14px}
    MuiInputBase-inputMultiline-281 {resize none; padding 0px}
    MuiInputBase-input-279 {font inherit; color currentcolor...}
    
    
    
    // Body
      @media(max-width: 767px) {
      body {
        padding-right: 0!important
      }
        .MuiOutlinedInput-multiline-327 {
        padding: 5px!important
      }
        .MuiInputBase-root-269 {
        line-height: 1.4em!important;
        padding: 5px!important
      }
        .MuiDialog-paper-5 {
        margin: 5px!important
      }
        .MuiTypography-title-227 {
        font-weight: 600;
        font-size: 20px
      }
        .emoji-mart-scroll {
        height: 150px
      }
        .emoji-mart-preview {
        position: relative;
        height: 60px
      }
      a#flashNotification {
        display: block;
        position: fixed;
        bottom: -1px;
        right: -1px;
        z - index: -1;
        opacity: 0;
        width: 1px;
        height: 1px
      }
    }
    
     
    
    
    
    
      @media(max-width: 767px) {
      body {
        padding-right: 0!important
      }
        .jss327 {
        padding: 5px!important
      }
        .jss269 {
        line-height: 1.4em!important;
        padding: 5px!important
      }
        .jss5 {
        margin: 5px!important
      }
        .jss227 {
        font-weight: 600;
        font-size: 20px
      }
        .emoji-mart-scroll {
        height: 150px
      }
        .emoji-mart-preview {
        position: relative;
        height: 60px
      }
      a#flashNotification {
        display: block;
        position: fixed;
        bottom: -1px;
        right: -1px;
        z - index: -1;
        opacity: 0;
        width: 1px;
        height: 1px
      }
    }
    
    */

    onIdle1 = (e) => {
        if (!this.state.openIsActiveDialog && localStorage.token) {
            this.setState({ openIsActiveDialog: true });
            this.startTimer();
        }
    };

    handleIsActiveDialogOpen = () => {
        this.setState({ openIsActiveDialog: true });
    };

    handleIsActiveDialogClose = () => {
        if (this.state.timeLeftInterval != null) {
            clearInterval(this.state.timeLeftInterval);
        }
        this.setState({ openIsActiveDialog: false });
    };
    
    handleUpdateStatusDialogOpen = () => {
        this.setState({ openUpdateStatusDialog: true });
    };

    handleUpdateStatusDialogClose = () => {
        this.setState({ openUpdateStatusDialog: false });
    };

    handleNetworkLossReload = () => {
        flashApp(this.props.preferences, 'flashstop');
        this.reloadKeepingSession(100);
    };

    
    startTimer = () => {
        var timeLeft = 30;
        //const now = Date.now();
        //const timeBeforeForcedLogout = 0.5 * 60 * 60 * 1000;
        //var updatedUser = { ...this.props.profile };
        var downloadTimer = setInterval(function () {
            //document.getElementById("countdown").innerHTML = timeLeft;
            timeLeft -= 1;
            if (timeLeft < 0) {
                if (this.state.openIsActiveDialog) {
                    this.setState({ openIsActiveDialog: false });
                    this.handleIsActiveDialogClose();

                    var updatedUser = { ...this.props.profile };
                    updatedUser["Status"] = "Done";
                    updatedUser["inOffice"] = false;
                    this.props.updateProfile(updatedUser);

                    flashApp(this.props.preferences, 'flashstop');
                    localStorage.removeItem("token");
                    this.props.logoutUser();
                    this.props.setChatDisplay(false);
                    setTimeout(() => window.location.reload(), 500);
                }
                clearInterval(downloadTimer);
            }
            
        }.bind(this), 1000);

        this.setState({ 'timeLeftInterval': downloadTimer });
        //
        this.closeChildWindows();
        //
    };
    
    
    // The countdown is state, rendered by the dialog. It used to write
    // document.getElementById("networkCountdown").innerHTML every second
    // from an interval nothing kept: if the network came back first the
    // dialog unmounted, the element was gone, and the interval threw every
    // second for the life of the page - and a second drop started another
    // on top of it. One countdown at a time now, cleared when the network
    // returns and on unmount.
    networkStartTimer = () => {
        if (this.networkTimeLeftInterval) {
            return;
        }
        var timeLeft = NETWORK_COUNTDOWN_SECONDS;
        this.setState({ networkSecondsLeft: timeLeft });
        this.networkTimeLeftInterval = setInterval(() => {
            timeLeft -= 1;
            if (timeLeft < 0) {
                this.stopNetworkTimer();
                if (this.state.isDisconnected) {
                    flashApp(this.props.preferences, 'flashstop');
                    this.reloadKeepingSession(500);
                }
                return;
            }
            this.setState({ networkSecondsLeft: timeLeft });
        }, 1000);
        //
        this.closeChildWindows();
        //
    };

    stopNetworkTimer = () => {
        clearInterval(this.networkTimeLeftInterval);
        this.networkTimeLeftInterval = null;
    };
    
    render() {
        let updateStatusDialog;
        if (this.props.profile.status === "Disconnected") {
            updateStatusDialog =
                <Dialog
                    open={this.state.openUpdateStatusDialog}
                    aria-labelledby="form-dialog-title"
                    style={{ margin: '5px !important' }}
                    PaperProps={{ style: { minWidth: '190px' } }}
                >
                    <DialogTitle id="form-dialog-title" style={{ paddingTop: '5px', paddingLeft: '5px', paddingBottom: '0px' }}>You've Disconnected!</DialogTitle>
                    <DialogContent style={{ paddingLeft: '5px', paddingRight: '5px', paddingBottom: '5px', paddingTop: '10px', fontSize: '11px' }}>
                        Please update your status when you have reconnected.
          </DialogContent>
                    <DialogActions style={{ margin: '0px' }}>
                        <Button onClick={this.handleUpdateStatusDialogClose} color="primary" style={{ margin: '0px', fontSize: '10px' }}>
                            OK
            </Button>
                    </DialogActions>
                </Dialog>;
        } else {
            updateStatusDialog = <div></div>;
        }

        let networkLossDialog =
            <Dialog
                open={true}
                aria-labelledby="form-dialog-title"
                style={{ margin: '5px !important' }}
                PaperProps={{ style: { minWidth: '190px' } }}
            >
                <DialogTitle id="form-dialog-title" style={{ paddingTop: '5px', paddingLeft: '5px', paddingBottom: '0px' }}>
                    <Typography style={{ fontSize: '14px', fontWeight: 'bold' }}>You've Disconnected!</Typography>
                </DialogTitle>
                <DialogContent style={{ paddingLeft: '5px', paddingRight: '5px', paddingBottom: '5px', paddingTop: '10px', fontSize: '11px' }}>
                    This page will refresh in <span id="networkCountdown">{this.state.networkSecondsLeft}</span> seconds. Please check your internet connection.
                </DialogContent>
            </Dialog>;
        {/*
      <Dialog
        open={true}
        aria-labelledby="form-dialog-title"
        style={{ margin: '5px !important' }}
        PaperProps={{ style: { minWidth: '190px' } }}
      >
        <DialogTitle id="form-dialog-title" style={{ paddingTop: '5px', paddingLeft: '5px', paddingBottom: '0px' }}>
          <Typography style={{ fontSize: '14px', fontWeight: 'bold' }}>You've Disconnected!</Typography>
        </DialogTitle>
        <DialogContent style={{ paddingLeft: '5px', paddingRight: '5px', paddingBottom: '5px', paddingTop: '10px', fontSize: '11px' }}>
          Please click OK to reload and check your internet connection.
        </DialogContent>
        <DialogActions style={{ margin: '0px', padding: '0px' }}>
          <Button onClick={this.handleNetworkLossReload} color="primary" style={{ marginRight: '5px', marginBottom: '5px', fontSize: '10px' }}>
            OK
          </Button>
        </DialogActions>
      </Dialog>;
      */}

        const { width } = this.props;
        return (
            <div>
                {/*this.state.isDisconnected && networkLossDialog && (this.props.setDisconnected(true) || true)*/}
                {this.state.isDisconnected && networkLossDialog}
                <IdleTimer
                    ref={ref => { this.idleTimer1 = ref }}
                    element={document}
                    onIdle={this.onIdle1}
                    debounce={250}
                    timeout={4 * 60 * 60 * 1000} // 4 hours
                    //timeout={10000} // 10 seconds
                />
                <Dialog
                    open={this.state.openIsActiveDialog}
                    aria-labelledby="form-dialog-title"
                    style={{ margin: '5px !important' }}
                    PaperProps={{ style: { minWidth: '190px' } }}
                >
                    <DialogTitle id="form-dialog-title" style={{ paddingTop: '5px', paddingLeft: '5px', paddingBottom: '0px' }}>
                        <Typography style={{ fontSize: '14px', fontWeight: 'bold' }}>Still Active?</Typography>
                    </DialogTitle>
                    <DialogContent style={{ paddingLeft: '5px', paddingRight: '5px', paddingBottom: '5px', paddingTop: '10px', fontSize: '11px' }}>
                        Please respond within <span id="countdown"></span> seconds.
                    </DialogContent>
                    <DialogActions style={{ margin: '0px', paddingTop: '0px' }}>
                        <Button onClick={this.handleIsActiveDialogClose} color="primary" style={{ margin: '0px', fontSize: '10px' }}>
                            Yes
                        </Button>
                    </DialogActions>
                </Dialog>
                {updateStatusDialog}
                <LoginPage />
                <MainSideBar className="test" />
            </div>
        );
    }
}

const mapDispatchToProps = dispatch => ({
    logoutUser: () => dispatch(logoutUser()),
    initiateConnection: (connection) => dispatch(initiateConnection['INITIATE_CONNECTION'](connection)),
    getProfileFetch: () => dispatch(getProfileFetch['LOAD_PROFILE']()),
    fetchMember: () => dispatch(memberActions['FETCH_MEMBERS']()),
    updateUnread: (message) => dispatch(userActions['SET_UNREAD_MESSAGE'](message)),
    setDisconnected: (bool) => dispatch(userActions['SET_DISCONNECTED'](bool)),
    //updateLastSeen: () => dispatch(userActions['UPDATE_LAST_SEEN']()),
    updateLastSeen: (updateStamp, options) => dispatch(userActions['UPDATE_LAST_SEEN'](updateStamp, options)),
    setChatDisplay: shouldDisplay => dispatch(windowActions['SET_CHAT_DISPLAY'](shouldDisplay)),
    fetchPreferences: () => dispatch(preferenceActions['FETCH_PREFERENCES']()),
    savePreferences: items => dispatch(preferenceActions['SAVE_PREFERENCES'](items)),
    setIconFlash: shouldTurnFlashOn => dispatch(windowActions['SET_ICON_FLASH'](shouldTurnFlashOn)),
    updateProfile: (user, options) => dispatch(getProfileFetch['UPDATE_PROFILE'](user, options)),
    setUnreadIds: unreadIds => dispatch(unreadActions['SET_UNREAD_IDS'](unreadIds)),
    getUnreadMessagesFromDb: () => dispatch(unreadActions['GET_UNREAD_IDS']()),
})

function mapStateToProps(state) {
    return {
        profile: state.profile,
        memberIdMap: state.memberIdMap,
        chatWindow: state.chatWindow,
        shouldAllowWindowAutoClose: state.shouldAllowWindowAutoClose,
        preferences: state.preferences,
        unreadMessages: state.unreadMessages,
        unreadLoaded: state.unreadLoaded,
        shouldTurnFlashOn: state.shouldTurnFlashOn,
        //shouldDisplayChat: state.shouldDisplayChat,
    };
}

App.propTypes = {
    width: PropTypes.string.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(withWidth()(App));

//export default connect(null, mapDispatchToProps)(App);


/*

Next Phase:
-Prompt user to update status when back by time is reached
-emoji :id: shortcut [ChatBoxCompose.js]
-bring chatbox up using focus
-Remove white bar in opened windows
-opens new chat window when double click after refreshing, even if chat is already opened (don't log out or refresh)
-when logout, auto set status to unavailable & close all opened windows (don't log out)
-close all open windows when logout or close memberList (don't log out)
-create a callback to parent component (sideMenuBar) losing MainMenu closes Bulletin window
-add spinner for save button (userManager and groupManager)
-when user is not back by their specified away time, dialog pops up to change status [UserInfo.js]
-away time formating from time picker is 24hr but need to display 12hr AM/PM [UserInfo.js; handleChange function]
-announcements
-connect to odyssey API for bulletin
-update bulletin with API calls throughout the day + motivational quote API
-permissions to search all conversations for admin


==== Features ====
-bulletin board
  -display sales progress & goals
  -motivational quotes API
-User manager
  -create a new user (name, extension, password, email)
  -set name, extension, password, permissions
  -change existing users
-Group mananger
  -create a group
  -add a member to group
  -remove a member from a group
  -list of all groups and their members
Login
  -user logs in via email/password
  -if enter invalid email/password, error message appears
  -when login successful, user will stay logged in for x amt of days
  -user can logout
  -press enter to login

Browser sidebar (MatterMost)
  -Expand1: list of all members, showing their status, hover for phone numbers
  -Expand2: Group Messages/Channels & Direct messages
    -unread messages (number of unread messages in browser tab & light up taskbar icon)
    -users' status
    -number people in the group
    -List group members' names... OR group name that they can create
    -hover over message to expose x to close the chat

Pop out member list
  -look & function same as SM list
  -displays name, extension, in/out of office, away message clock icon, who is oncall via MM logo icon
  -hover over name for name and phone numbers
  -pop out button should be bottom of left side bar
  -show & change user's status on pop-out list

User status + Away message
  -user selects from 4 different statuses next to user's name
  -user can enter in away message + away time (optional)
  -away message includes a text box and a time picker to select what time they will be back (how to handle when some people say ASAP?)
  -when that time comes, show pop up alert propmpting user to update status (will users still receive messages while alert is not clicked?)
  -everytime they login, should status be available?
  -when change status, should be reflected in list
  -away message -> cancel: doesn't change status to busy
                -> no message: unbold, black font color name
               -> ok: unbold, black font color name, clock symbol, message & time required
  -when submit away mssg & back time, user will be prompted to change their status at the time they inputed
  
Edit profile
  -user can edit their profile (name, address, phone numbers, email, extension) (email address that they login with)
  -user can edit their work schedule (should this be time pickers too?)

On-call schedule
  -only available to those with group/role Admin
  -select on-call schedule for each company (MarketMaps, GBBIS, etc.)
  -persist on-call schedule from previous day
  -emails on-call schedule to specified recipients 
  -specified recipients vary between each company (persist the email recipients for each company)
  -what if no email recipients are selected and press "send email"?

Chat messaging
  -chat instant messaging box by double clicking user's name on pop-out list
  -only one chat box; multiple tabs for multiple chats
  -chat messaging window in browser tab
  -lists direct messages + group direct messages, private channels, public channels
  -emojis

User Manage + Manage Groups
  -UI to add a new employee to TeamX
  -Set up their name, phone extension, password, email, user permissions (Allow the ability to manage users, Allow the ability to change the on call schedule, Allow the ability to view log files)
  -UI for adding a member to a group (ex: Admin, Developers, Sales, etc.)

View progress and goals
  -displays company sales via percentage bars, dollar amt goal, time frame, this year, last year
  -motivational quote
  -business days left (There is 1 day left in the week, There are 16 days left in the month, etc.)
  


App
 |
 | __ LoginPage
 |
 | __ MainSideBar
 |     |
 |     | __ UserHub (logout, main menu, bulletin, user/group manager)
 |     |     |
 |     |     | __ UserInfo (user's name, avatar, status symbol, change status, add away message)
 |     |     |
 |     |     | __ MainMenu (options: edit profile/schedule, select on-call schedule if admin role, logout)
 |     |           |
 |     |           | __ Profile (edit your profile/schedule)
 |     |           |
 |     |           | __ Bulletin
 |     |           |
 |     |           | __ OnCallScheduleList (on hold)
 |     |                 |
 |     |                 | __ OnCallScheduleItem (on hold)
 |     |
 |     | __ MemberList
 |           | 
 |           | __ MemberListItem
 |           |
 |           | __ ChatBoxTabsWindow (fetches messages array and passes it to MessageList which passes it to MessageListItem)
 |                  |
 |                  | __ ChatBoxMessageList
 |                  |     |
 |                  |     | __ ChatBoxMessageListItem
 |                  |
 |                  | __ ChatBoxCompose (adds message obj to messages array)
 |     





 |     | __ DirectMessageList (Tab2)
 |           |
 |           | __DirectMessageListItem
 |     
 | __ WideChatWindow
 |     |
 |     | __ WideChatTopBar
 |     |
 |     | __ WideChatMessageDisplay
 |     |     |
 |     |     | __ WideChatMessageDisplayItem
 |     |
 |     | __ WideChatMessageCompose
 */


// WEBPACK FOOTER //
// ./src/App.js